import { useState, useEffect, useMemo } from 'react'
import type { TenantStandardAssignment, UpdateStandardConfigRequest, FrameworkScoreDisplay } from '@/types'
import { IconButton } from '@/components/carbon/IconButton'
import { IconHelp } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Modal } from '@/components/carbon/Modal'
import { NumberInput } from '@/components/carbon/NumberInput'
import { Toggle } from '@/components/carbon/Toggle'
import { FRAMEWORK_ORDER } from '@/lib/frameworkUtils'

interface StandardsConfigModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: TenantStandardAssignment[]
  onSave: (updates: UpdateStandardConfigRequest[]) => void
  isSaving?: boolean
  /** Frameworks with scoring data (for star toggles) */
  frameworks?: FrameworkScoreDisplay[]
  /** Callback when a framework star is toggled */
  onToggleFrameworkStar?: (framework: FrameworkScoreDisplay) => void
}

interface StandardConfig {
  standardId: string
  standardCode: string
  standardName: string
  isEnabled: boolean
  importance: number // 1-10 scale
  includeInGuardianScore: boolean
}

/** Convert backend weight (0-100) to importance (1-10). Clamps to valid range. */
function weightToImportance(weight: number): number {
  // Map 0-100 → 1-10. Round to nearest integer.
  const imp = Math.round(weight / 10)
  return Math.max(1, Math.min(10, imp || 5)) // default 5 if 0/NaN
}

/** Convert importance (1-10) back to backend weight (0-100). */
function importanceToWeight(importance: number): number {
  return importance * 10
}

/** Labels for importance levels */
function importanceLabel(value: number): string {
  if (value <= 2) return 'Low'
  if (value <= 4) return 'Below average'
  if (value <= 6) return 'Average'
  if (value <= 8) return 'Above average'
  return 'Critical'
}

// Metadata for each workload standard
const WORKLOAD_METADATA: Record<string, { policies: number; frameworks: string[]; description: string }> = {
  GUARDIAN_IDENTITY: {
    policies: 30,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB', 'EIDSCA'],
    description: 'Entra ID identity and access management policies',
  },
  GUARDIAN_EXCHANGE: {
    policies: 17,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Exchange Online security policies',
  },
  GUARDIAN_DEFENDER: {
    policies: 16,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Microsoft Defender for Office 365 policies',
  },
  GUARDIAN_TEAMS: {
    policies: 20,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Microsoft Teams collaboration policies',
  },
  GUARDIAN_SHAREPOINT: {
    policies: 18,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'SharePoint and OneDrive sharing policies',
  },
}

/**
 * Preferred ordering for workloads - matches the overview page.
 * Identity first as it typically has the most policies and highest impact.
 */
const WORKLOAD_ORDER: Record<string, number> = {
  GUARDIAN_IDENTITY: 0,
  GUARDIAN_EXCHANGE: 1,
  GUARDIAN_DEFENDER: 2,
  GUARDIAN_TEAMS: 3,
  GUARDIAN_SHAREPOINT: 4,
}

/**
 * Modal for configuring which workloads to evaluate and which frameworks
 * to include in the Guardian Score, on the Carbon Modal (layer-02, pinned
 * header, Cancel and Save as the footer button set).
 *
 * Two sections:
 * 1. Frameworks - a toggle includes or excludes each from the Guardian Score
 *    (it writes through at once, as the framework tiles' star did)
 * 2. Workloads - an evaluation toggle and an importance (1-10) per workload,
 *    saved together
 */
export default function StandardsConfigModal({
  isOpen,
  onClose,
  assignments,
  onSave,
  isSaving = false,
  frameworks,
  onToggleFrameworkStar,
}: StandardsConfigModalProps) {
  // Local state for editing workload configs
  const [configs, setConfigs] = useState<StandardConfig[]>([])
  const [showInfoFlyout, setShowInfoFlyout] = useState(false)

  // Sort and initialize configs from assignments
  useEffect(() => {
    if (assignments.length > 0) {
      const sorted = [...assignments]
        .sort((a, b) => {
          const orderA = WORKLOAD_ORDER[a.standardCode] ?? 99
          const orderB = WORKLOAD_ORDER[b.standardCode] ?? 99
          return orderA - orderB
        })
        .map((a) => ({
          standardId: a.standardId,
          standardCode: a.standardCode,
          standardName: a.standardName,
          isEnabled: a.isEnabled,
          importance: weightToImportance(a.weight),
          includeInGuardianScore: a.includeInGuardianScore,
        }))
      setConfigs(sorted)
    }
  }, [assignments])

  // Sort frameworks to match the overview page order
  const sortedFrameworks = useMemo(() => {
    if (!frameworks) return []
    return [...frameworks]
      .filter((f) => !f.isGuardian)
      .sort((a, b) => {
        const orderA = FRAMEWORK_ORDER[a.code] ?? 99
        const orderB = FRAMEWORK_ORDER[b.code] ?? 99
        return orderA - orderB
      })
  }, [frameworks])

  // Count starred frameworks
  const starredCount = sortedFrameworks.filter((f) => f.includeInGuardianScore).length

  // Update a specific config
  const updateConfig = (standardId: string, updates: Partial<StandardConfig>) => {
    setConfigs((prev) =>
      prev.map((c) => (c.standardId === standardId ? { ...c, ...updates } : c))
    )
  }

  // Toggle enabled state
  const toggleEnabled = (standardId: string) => {
    updateConfig(standardId, {
      isEnabled: !configs.find((c) => c.standardId === standardId)?.isEnabled,
    })
  }

  // Update importance
  const updateImportance = (standardId: string, importance: number) => {
    updateConfig(standardId, { importance })
  }

  // Handle save - convert importance back to weight for the backend
  const handleSave = () => {
    const updates: UpdateStandardConfigRequest[] = configs.map((c) => {
      // Star state (includeInGuardianScore) is owned by the framework toggles in
      // the top section, which write through their own immediate mutation. Read
      // the LIVE value from the frameworks prop so Save can't revert a toggle
      // made after the modal opened (configs snapshot the flag once at mount).
      // MCSB is always included in the Guardian Score, so force it on
      // regardless — this is what previously got silently knocked off when an
      // unrelated Save reused MCSB's stale snapshot.
      const liveFramework = frameworks?.find((f) => f.standardId === c.standardId)
      const isMcsb = (c.standardCode ?? '').toUpperCase() === 'MCSB'
      const includeInGuardianScore = isMcsb
        ? true
        : liveFramework?.includeInGuardianScore ?? c.includeInGuardianScore
      return {
        standardId: c.standardId,
        isEnabled: c.isEnabled,
        weight: importanceToWeight(c.importance),
        includeInGuardianScore,
      }
    })
    onSave(updates)
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title="Configure Best Practice checks"
      subtitle="Choose the frameworks in your Guardian Score and the workloads Guardian evaluates."
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      primaryAction={{ label: 'Save configuration', onClick: handleSave, loading: isSaving }}
    >
      <div className="space-y-8">
        {/* Framework scoring */}
        {sortedFrameworks.length > 0 && (
          <section aria-labelledby="config-frameworks-h">
            <h3 id="config-frameworks-h" className="m-0 text-body font-semibold text-text-primary">
              Frameworks in your Guardian Score
            </h3>
            <p className="m-0 mt-1 text-helper text-text-helper">
              {starredCount > 0
                ? `${starredCount} of ${sortedFrameworks.length} included. A change here applies at once.`
                : 'No frameworks included yet. A change here applies at once.'}
            </p>
            <ul className="m-0 mt-3 grid list-none grid-cols-1 gap-px bg-line-subtle p-0 c-md:grid-cols-2">
              {sortedFrameworks.map((framework) => (
                <li key={framework.standardId} className="flex min-w-0 items-start justify-between gap-4 bg-layer-1 p-4">
                  <div className="min-w-0">
                    <p className="m-0 truncate text-body font-semibold text-text-primary">{framework.name}</p>
                    <p className="m-0 mt-0.5 text-helper tabular-nums text-text-helper">
                      {Math.round(framework.score)}% · {framework.passedControls} of {framework.totalControls} controls pass
                    </p>
                  </div>
                  <Toggle
                    label={`Include ${framework.name} in the Guardian Score`}
                    hideLabel
                    onText="In score"
                    offText="Not in score"
                    checked={framework.includeInGuardianScore}
                    onChange={() => onToggleFrameworkStar?.(framework)}
                    disabled={!onToggleFrameworkStar}
                    className="flex-none"
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Workloads */}
        <section aria-labelledby="config-workloads-h">
          <div className="flex items-center gap-1">
            <h3 id="config-workloads-h" className="m-0 text-body font-semibold text-text-primary">
              Microsoft 365 workloads
            </h3>
            <IconButton
              label="What is workload importance?"
              icon={<IconHelp />}
              size={32}
              aria-expanded={showInfoFlyout}
              onClick={() => setShowInfoFlyout(!showInfoFlyout)}
            />
          </div>
          <p className="m-0 mt-1 text-helper text-text-helper">
            Turn evaluation on or off per workload, and set how much each one weighs in your scores.
          </p>

          {showInfoFlyout && (
            <InlineNotification
              kind="info"
              title="How workload importance works."
              onClose={() => setShowInfoFlyout(false)}
              closeLabel="Close the importance note"
              className="mt-3"
            >
              Importance sets how much each workload counts toward your compliance scores, so a workload at 10 (critical)
              moves your score more than one at 3 (below average). The values are relative: workloads at the same number
              count equally. Raise a workload&rsquo;s importance when failures there should weigh more. 1-2 is low, 3-4
              below average, 5-6 average, 7-8 above average and 9-10 critical.
            </InlineNotification>
          )}

          <ul className="m-0 mt-3 list-none space-y-px p-0">
            {configs.map((config) => {
              const meta = WORKLOAD_METADATA[config.standardCode]
              return (
                <li key={config.standardId} className="bg-layer-1 p-4">
                  <Toggle
                    label={config.standardName}
                    checked={config.isEnabled}
                    onChange={() => toggleEnabled(config.standardId)}
                    onText="Evaluated"
                    offText="Not evaluated"
                    helper={
                      meta
                        ? `${meta.description}. ${meta.policies} policies · ${meta.frameworks.join(', ')}`
                        : undefined
                    }
                  />

                  {/* Importance (only while the workload is evaluated) */}
                  {config.isEnabled && (
                    <NumberInput
                      label="Importance, 1 to 10"
                      helper={importanceLabel(config.importance)}
                      min={1}
                      max={10}
                      step={1}
                      value={config.importance}
                      onChange={(e) => {
                        const next = parseInt(e.target.value, 10)
                        if (!Number.isNaN(next)) updateImportance(config.standardId, Math.max(1, Math.min(10, next)))
                      }}
                      wrapperClassName="mt-4 max-w-[12rem]"
                    />
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      </div>
    </Modal>
  )
}
